import React, { useMemo, useRef } from "react";
import PropTypes from "prop-types";
import algoliasearch from "algoliasearch/lite";
import { InstantSearch, SearchBox, RefinementList, ToggleRefinement } from "react-instantsearch";
import InfiniteHits from "./infinite_hits";
import SearchToolbar from "../../source_directory/SearchToolbar";
import FilterPill from "../../source_directory/FilterPill";
import FilterClearEffect from "../../source_directory/FilterClearEffect";
import sharedAlgoliaRouter from "../../source_directory/sharedAlgoliaRouter";
import useSavedOnlyFilter from "../../source_directory/useSavedOnlyFilter";
import DefaultIndexConfigure from "../../source_directory/DefaultIndexConfigure";
import withUnfilteredDefaultFacets from "../../source_directory/withUnfilteredDefaultFacets";
import useDebouncedQueryHook from "../../source_directory/useDebouncedQueryHook";

function CompaniesSearch({
  algoliaAppId,
  algoliaSearchKey,
  indexName,
  viewMode,
  showFilters,
  onToggleFilters,
  savedOnly,
  onToggleSavedOnly,
  onViewModeChange,
  groupCount,
  onMyGroupsClick,
}) {
  const queryHook = useDebouncedQueryHook();
  const searchClient = useMemo(
    () => withUnfilteredDefaultFacets(algoliasearch(algoliaAppId, algoliaSearchKey)),
    [algoliaAppId, algoliaSearchKey],
  );
  const savedFilter = useSavedOnlyFilter(savedOnly, "Id::PromoEntity::Company");

  const getSearchParamsFromUrl = () => {
    const url = new URL(window.location.href);
    return {
      query: url.searchParams.get("query"),
      expertName: url.searchParams.get("expert_name"),
      title: url.searchParams.get("title"),
      prAgency: url.searchParams.get("pr_agency"),
      companyType: url.searchParams.get("company_type"),
    };
  };

  // Rewrite friendly deep-link params into Algolia's routing format synchronously during the first
  // render, before the child InstantSearch reads the URL on mount. A useEffect would run too late.
  const didTransformUrl = useRef(false);
  if (!didTransformUrl.current) {
    didTransformUrl.current = true;
    const currentUrl = new URL(window.location.href);
    const hasAlgoliaParams = Array.from(currentUrl.searchParams.keys()).some((key) => key.includes(`${indexName}[`));
    const searchParams = getSearchParamsFromUrl();
    const hasSearchParams = Object.values(searchParams).some((value) => value !== null);

    if (hasSearchParams && !hasAlgoliaParams) {
      const newUrl = new URL(window.location.href);
      newUrl.search = "";
      if (searchParams.query) newUrl.searchParams.set(`${indexName}[query]`, searchParams.query);
      if (searchParams.expertName)
        newUrl.searchParams.set(`${indexName}[refinementList][experts.name][0]`, searchParams.expertName);
      if (searchParams.title)
        newUrl.searchParams.set(`${indexName}[refinementList][experts.title][0]`, searchParams.title);
      if (searchParams.prAgency)
        newUrl.searchParams.set(`${indexName}[refinementList][agencies.name][0]`, searchParams.prAgency);
      if (searchParams.companyType === "pr_agencies")
        newUrl.searchParams.set(`${indexName}[toggle][is_pr_agency]`, true);
      window.history.replaceState(null, "", newUrl.toString());
    }
  }

  return (
    <div className="ais-InstantSearch">
      <InstantSearch
        indexName={indexName}
        searchClient={searchClient}
        routing={{ router: sharedAlgoliaRouter }}
        future={{ preserveSharedStateOnUnmount: true, cleanUrlOnDispose: false }}
      >
        <DefaultIndexConfigure baseFilter={savedFilter} unfilteredFacets={showFilters} />
        <FilterClearEffect showFilters={showFilters} />
        <SearchToolbar
          showFilters={showFilters}
          onToggleFilters={onToggleFilters}
          savedOnly={savedOnly}
          onToggleSavedOnly={onToggleSavedOnly}
          viewMode={viewMode}
          onViewModeChange={onViewModeChange}
          groupCount={groupCount}
          onMyGroupsClick={onMyGroupsClick}
        >
          <SearchBox autoFocus placeholder="Search" searchAsYouType queryHook={queryHook} />
        </SearchToolbar>

        <div
          className="sd-filter-row"
          style={{ display: showFilters ? "flex" : "none", flexWrap: "wrap", gap: 8, marginBottom: 12 }}
        >
          <FilterPill label="Type" attribute="is_pr_agency">
            <div style={{ minWidth: 160 }}>
              <ToggleRefinement attribute="is_pr_agency" label="PR Agencies only" />
            </div>
          </FilterPill>
          <FilterPill label="Experts" attribute="experts.name">
            <RefinementList attribute="experts.name" limit={10} showMore searchable />
          </FilterPill>
        </div>

        <InfiniteHits viewMode={viewMode} />
      </InstantSearch>
    </div>
  );
}

CompaniesSearch.propTypes = {
  algoliaAppId: PropTypes.string.isRequired,
  algoliaSearchKey: PropTypes.string.isRequired,
  indexName: PropTypes.string.isRequired,
  viewMode: PropTypes.oneOf(["grid", "list"]).isRequired,
  showFilters: PropTypes.bool.isRequired,
  onToggleFilters: PropTypes.func.isRequired,
  savedOnly: PropTypes.bool,
  onToggleSavedOnly: PropTypes.func.isRequired,
  onViewModeChange: PropTypes.func.isRequired,
  groupCount: PropTypes.number,
  onMyGroupsClick: PropTypes.func,
};

export default CompaniesSearch;
